/**
 * Slice 9.3 / per-asset crypto management — Asset-centric crypto inventory panel.
 *
 * Renders on the Edit Asset page. Calls
 *   GET /api/v1/projects/:projectId/assets/:assetId/crypto-artifacts
 * and shows all crypto artifacts on this asset, grouped by type.
 *
 * This panel also MANAGES the asset's crypto artifacts directly — the asset is
 * fixed by the page you're on, so you never enter an asset id:
 *   - Add one artifact   → POST   /projects/:p/assets/:a/crypto-artifacts/:type
 *   - Import a CSV        → POST   /projects/:p/assets/:a/crypto-artifacts/:type/import
 *   - Remove an artifact  → DELETE /projects/:p/assets/:a/crypto-artifacts/:type/:id
 * Risk classification comes from the bundled NIST/IETF Cryptography Knowledge
 * Base and is applied automatically on add/import.
 */

import { CryptoPartsCell } from './crypto-parts-cell';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, postCsv } from '../api/client';

type ArtifactType = 'key' | 'certificate' | 'protocol' | 'cipher_suite' | 'library' | 'store' | 'signature';

/** Groups the asset inventory lists: every editable type, plus SSH algorithms and algorithms in code (unified import only, not edited here). */
type GroupType = ArtifactType | 'ssh_algorithm' | 'algorithm';

const TYPE_LABELS: Record<GroupType, string> = {
  key: 'Keys',
  certificate: 'Certificates',
  protocol: 'Protocols',
  cipher_suite: 'Cipher suites',
  library: 'Libraries',
  store: 'Key stores',
  signature: 'Signatures',
  ssh_algorithm: 'SSH algorithms',
  algorithm: 'Algorithms in code',
};

const ARTIFACT_TYPES: ArtifactType[] = [
  'key', 'certificate', 'protocol', 'cipher_suite', 'library', 'store', 'signature',
];

// ── Per-type form fields. Field names match the backend importer's columns
// exactly, so a manual add flows through the same validation + KB scoring. ──
type FieldDef = {
  name: string;
  label: string;
  kind: 'text' | 'number' | 'select' | 'bool' | 'textarea';
  required?: boolean;
  options?: string[];
  placeholder?: string;
};

const FIELD_DEFS: Record<ArtifactType, FieldDef[]> = {
  key: [
    { name: 'name', label: 'Name', kind: 'text' },
    { name: 'algorithm_raw', label: 'Algorithm', kind: 'text', required: true, placeholder: 'e.g. RSA, ECDSA, AES' },
    { name: 'key_type', label: 'Key type', kind: 'select', required: true, options: ['Public', 'Private', 'Symmetric', 'Other'] },
    { name: 'key_length', label: 'Key length (bits)', kind: 'number' },
    { name: 'curve_name', label: 'Curve', kind: 'text', placeholder: 'e.g. P-256' },
    { name: 'key_usage', label: 'Key usage', kind: 'text' },
    { name: 'file_path', label: 'File path', kind: 'text' },
    { name: 'file_format', label: 'File format', kind: 'text' },
    { name: 'notes', label: 'Notes', kind: 'textarea' },
  ],
  certificate: [
    { name: 'name', label: 'Name', kind: 'text' },
    { name: 'subject', label: 'Subject', kind: 'text' },
    { name: 'issuer', label: 'Issuer', kind: 'text' },
    { name: 'serial_number', label: 'Serial number', kind: 'text' },
    { name: 'signature_algorithm_raw', label: 'Signature algorithm', kind: 'text', required: true },
    { name: 'hashing_algorithm_raw', label: 'Hashing algorithm', kind: 'text' },
    { name: 'public_key_algorithm_raw', label: 'Public key algorithm', kind: 'text', required: true },
    { name: 'public_key_length', label: 'Public key length', kind: 'number' },
    { name: 'curve_name', label: 'Curve', kind: 'text' },
    { name: 'notes', label: 'Notes', kind: 'textarea' },
  ],
  protocol: [
    { name: 'name', label: 'Name', kind: 'text' },
    { name: 'protocol_family', label: 'Protocol family', kind: 'text', required: true, placeholder: 'e.g. TLS, SSH, IPsec' },
    { name: 'protocol_version_raw', label: 'Version', kind: 'text', required: true, placeholder: 'e.g. 1.2' },
    { name: 'endpoint', label: 'Endpoint', kind: 'text' },
    { name: 'configuration_notes', label: 'Configuration notes', kind: 'text' },
    { name: 'notes', label: 'Notes', kind: 'textarea' },
  ],
  cipher_suite: [
    { name: 'name', label: 'Name', kind: 'text' },
    { name: 'suite_raw', label: 'Suite', kind: 'text', required: true, placeholder: 'e.g. TLS_AES_128_GCM_SHA256' },
    { name: 'iana_code', label: 'IANA code', kind: 'text' },
    { name: 'kex_algorithm', label: 'Key exchange', kind: 'text' },
    { name: 'authentication_algorithm', label: 'Authentication', kind: 'text' },
    { name: 'bulk_encryption_algorithm', label: 'Bulk encryption', kind: 'text' },
    { name: 'mac_or_aead', label: 'MAC / AEAD', kind: 'text' },
    { name: 'notes', label: 'Notes', kind: 'textarea' },
  ],
  library: [
    { name: 'name', label: 'Name', kind: 'text', required: true },
    { name: 'vendor', label: 'Vendor', kind: 'text' },
    { name: 'version_raw', label: 'Version', kind: 'text', required: true },
    { name: 'install_path', label: 'Install path', kind: 'text' },
    { name: 'is_fips_validated', label: 'FIPS validated', kind: 'bool' },
    { name: 'has_pqc_support', label: 'Has PQC support', kind: 'bool' },
    { name: 'end_of_life_date', label: 'End-of-life date', kind: 'text', placeholder: 'YYYY-MM-DD' },
    { name: 'notes', label: 'Notes', kind: 'textarea' },
  ],
  store: [
    { name: 'name', label: 'Name', kind: 'text', required: true },
    { name: 'store_type', label: 'Store type', kind: 'select', required: true, options: ['PKCS11', 'JCEKS', 'PKCS12', 'HSM', 'TPM', 'KMS', 'File', 'Database', 'Cloud_KMS', 'Other'] },
    { name: 'vendor', label: 'Vendor', kind: 'text' },
    { name: 'fips_level', label: 'FIPS level', kind: 'select', options: ['Unknown', 'None', 'Level_1', 'Level_2', 'Level_3', 'Level_4'] },
    { name: 'has_pqc_support', label: 'Has PQC support', kind: 'bool' },
    { name: 'location', label: 'Location', kind: 'text' },
    { name: 'key_count', label: 'Key count', kind: 'number' },
    { name: 'notes', label: 'Notes', kind: 'textarea' },
  ],
  signature: [
    { name: 'name', label: 'Name', kind: 'text' },
    { name: 'signature_purpose', label: 'Purpose', kind: 'select', required: true, options: ['JWT', 'Code_signing', 'Document', 'Email', 'API_request', 'Software_artifact', 'Other'] },
    { name: 'signature_algorithm_raw', label: 'Signature algorithm', kind: 'text', required: true },
    { name: 'hashing_algorithm_raw', label: 'Hashing algorithm', kind: 'text' },
    { name: 'key_length', label: 'Key length', kind: 'number' },
    { name: 'curve_name', label: 'Curve', kind: 'text' },
    { name: 'notes', label: 'Notes', kind: 'textarea' },
  ],
};

// Example values used to seed a downloadable CSV template per type.
const TEMPLATE_EXAMPLES: Record<ArtifactType, Record<string, string>> = {
  key: { name: 'example-key', algorithm_raw: 'RSA', key_type: 'Public', key_length: '2048' },
  certificate: { name: 'example-cert', signature_algorithm_raw: 'RSA', public_key_algorithm_raw: 'RSA', public_key_length: '2048' },
  protocol: { name: 'example-endpoint', protocol_family: 'TLS', protocol_version_raw: '1.2' },
  cipher_suite: { name: 'example-suite', suite_raw: 'TLS_AES_128_GCM_SHA256' },
  library: { name: 'OpenSSL', vendor: 'OpenSSL', version_raw: '3.0.0' },
  store: { name: 'example-store', store_type: 'PKCS12' },
  signature: { name: 'example-signature', signature_purpose: 'JWT', signature_algorithm_raw: 'RS256' },
};

function csvCell(v: string): string {
  return /[",\n]/.test(v) ? '"' + v.replace(/"/g, '""') + '"' : v;
}

// Map a stored artifact row back into editable form values for its type.
export function rowToForm(type: ArtifactType, row: Record<string, unknown>): Record<string, string | boolean> {
  const out: Record<string, string | boolean> = {};
  for (const d of FIELD_DEFS[type]) {
    const v = row[d.name];
    if (d.kind === 'bool') out[d.name] = v === 1 || v === true || v === '1';
    else if (v === null || v === undefined) out[d.name] = '';
    else out[d.name] = String(v);
  }
  return out;
}

// Build + download a CSV template for the chosen type. Columns are exactly the
// fields the importer accepts; there is intentionally NO asset column (the
// asset comes from the page). One pre-filled example row shows the format.
function downloadCsvTemplate(type: ArtifactType): void {
  const cols = FIELD_DEFS[type].map((f) => f.name);
  const ex = TEMPLATE_EXAMPLES[type] ?? {};
  const csv = cols.join(',') + '\n' + cols.map((c) => csvCell(ex[c] ?? '')).join(',') + '\n';
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `crypto-${type}-template.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const BTN_PRIMARY = 'text-sm bg-accent text-white px-3 py-1.5 rounded hover:bg-accent-hover disabled:opacity-50';
const BTN_SECONDARY = 'text-sm border border-ink-200 rounded px-3 py-1.5 bg-white text-ink hover:bg-ink-50 disabled:opacity-50';
const INPUT = 'w-full border border-ink-200 rounded px-2 py-1 text-sm';
const LABEL = 'block text-xs text-ink-600 mb-1';

interface InventoryGroup {
  type: GroupType;
  count: number;
  rows: Record<string, unknown>[];
}

interface InventoryResponse {
  asset: { id: string; name: string };
  total_artifacts: number;
  groups: InventoryGroup[];
  /** Operations, formats and findings from a source-code scan: not artifacts, shown with the file they came from. */
  code_notes?: CodeNote[];
}

interface CodeNote { id: string; kind: 'operation' | 'format' | 'finding'; name: string; file_path: string | null; observation_count: number | null; risk_level: string | null }

interface ImportSummary {
  imported?: number;
  failed?: number;
}

export function CryptoInventoryPanel({
  projectId, assetId,
}: { projectId: string; assetId: string }) {
  const qc = useQueryClient();
  const [panel, setPanel] = useState<'none' | 'add' | 'import'>('none');
  const [editTarget, setEditTarget] = useState<{ type: ArtifactType; row: Record<string, unknown> } | null>(null);

  const q = useQuery<InventoryResponse>({
    queryKey: ['asset-crypto-inventory', projectId, assetId],
    queryFn: () => api.get<InventoryResponse>(
      `/projects/${projectId}/assets/${assetId}/crypto-artifacts`,
    ),
  });

  const del = useMutation({
    mutationFn: ({ type, id }: { type: ArtifactType; id: string }) =>
      api.delete<void>(`/projects/${projectId}/assets/${assetId}/crypto-artifacts/${type}/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['asset-crypto-inventory', projectId, assetId] }),
  });
  const deletingId = del.isPending ? (del.variables as { id: string } | undefined)?.id ?? null : null;

  function handleDelete(type: ArtifactType, id: string, name: string) {
    if (window.confirm(`Remove "${name || 'this artifact'}" from this asset? This cannot be undone.`)) {
      del.mutate({ type, id });
    }
  }

  function handleEdit(type: ArtifactType, row: Record<string, unknown>) {
    setEditTarget({ type, row });
    setPanel('none');
  }

  if (q.isLoading) {
    return (
      <section className="mt-8 border-t border-ink-200 pt-8">
        <h2 className="text-xl font-display text-ink mb-2">Crypto inventory</h2>
        <p className="text-sm text-ink-500 italic">Loading…</p>
      </section>
    );
  }
  if (q.isError) {
    return (
      <section className="mt-8 border-t border-ink-200 pt-8">
        <h2 className="text-xl font-display text-ink mb-2">Crypto inventory</h2>
        <p className="text-sm text-red-700">Failed to load crypto inventory.</p>
      </section>
    );
  }
  const data = q.data!;
  const populated = data.groups.filter((g) => g.count > 0);

  return (
    <section className="mt-8 border-t border-ink-200 pt-8">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-xl font-display text-ink">Crypto inventory</h2>
        <p className="text-sm text-ink-500">
          {data.total_artifacts} artifact{data.total_artifacts !== 1 ? 's' : ''} on this asset
        </p>
      </div>
      <p className="text-xs text-ink-500 mb-4">
        All cryptographic artifacts associated with this asset, grouped by type.
        Artifacts you add or import here are linked to this asset automatically —
        you never enter an asset id. Risk classifications come from the bundled
        NIST/IETF Cryptography Knowledge Base.
      </p>

      {/* Manage actions */}
      <div className="flex gap-2 mb-4">
        <button
          type="button"
          className={BTN_PRIMARY}
          onClick={() => setPanel(panel === 'add' ? 'none' : 'add')}
        >
          + Add artifact
        </button>
        <button
          type="button"
          className={BTN_SECONDARY}
          onClick={() => setPanel(panel === 'import' ? 'none' : 'import')}
        >
          Import CSV
        </button>
        {del.isError && (
          <span className="text-xs text-red-700 self-center">
            Delete failed. Please try again.
          </span>
        )}
      </div>

      {editTarget && (
        <ArtifactForm
          mode="edit"
          projectId={projectId}
          assetId={assetId}
          fixedType={editTarget.type}
          initial={rowToForm(editTarget.type, editTarget.row)}
          artifactId={String(editTarget.row['id'] ?? '')}
          onClose={() => setEditTarget(null)}
        />
      )}
      {!editTarget && panel === 'add' && (
        <ArtifactForm mode="add" projectId={projectId} assetId={assetId} onClose={() => setPanel('none')} />
      )}
      {!editTarget && panel === 'import' && (
        <ImportForm projectId={projectId} assetId={assetId} onClose={() => setPanel('none')} />
      )}

      {populated.length === 0 ? (
        <div className="border border-dashed border-ink-200 rounded p-6 text-center">
          <p className="text-ink-500 text-sm">
            No cryptographic artifacts inventoried for this asset yet.
          </p>
          <p className="text-ink-400 text-xs mt-2">
            Use <span className="font-medium">+ Add artifact</span> for a single entry,
            or <span className="font-medium">Import CSV</span> to add several at once.
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          {populated.map((g) => (
            <ArtifactGroupTable
              key={g.type}
              group={g}
              onEdit={handleEdit}
              onDelete={handleDelete}
              deletingId={deletingId}
            />
          ))}
        </div>
      )}
      {(data.code_notes ?? []).length > 0 && <CodeNotes notes={data.code_notes!} />}
    </section>
  );
}

const NOTE_LABEL: Record<CodeNote['kind'], string> = { finding: 'Finding', operation: 'Operation', format: 'Format' };

/** What the source-code scan saw besides algorithms, by file: findings first, then operations and formats. Not artifacts. */
function CodeNotes({ notes }: { notes: CodeNote[] }) {
  const byFile = new Map<string, CodeNote[]>();
  for (const n of notes) byFile.set(n.file_path ?? '', [...(byFile.get(n.file_path ?? '') ?? []), n]);
  return (
    <div className="mt-5" data-testid="code-notes">
      <h3 className="text-sm font-semibold text-ink mb-1">Code notes ({notes.length})</h3>
      <p className="text-xs text-ink-500 mb-2">
        Operations, formats and findings the source-code scan reported, with the file they were seen in. They belong with the
        algorithms of the same file; they are not crypto artifacts and are not counted or scored.
      </p>
      <div className="border border-ink-200 rounded divide-y divide-ink-100 text-xs">
        {[...byFile].map(([file, list]) => (
          <div key={file} className="px-3 py-2">
            <p className="font-mono text-ink-500 break-all">{file || '(no file recorded)'}</p>
            <p className="mt-1 flex flex-wrap gap-1">
              {list.map((n) => (
                <span key={n.id} title={NOTE_LABEL[n.kind]}
                  className={`px-1.5 py-0.5 rounded border ${n.kind === 'finding' ? 'border-red-200 text-red-800 bg-red-50' : 'border-ink-200 text-ink-700'}`}>
                  {n.kind === 'finding' ? `${NOTE_LABEL.finding}: ` : ''}{n.name}{n.observation_count && n.observation_count > 1 ? ` ×${n.observation_count}` : ''}
                </span>
              ))}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Add a single artifact (manual entry) — flows through the same KB-scored
// pipeline as the importer. The asset is fixed by the page, never entered.
// ─────────────────────────────────────────────────────────────────────────
export function ArtifactForm({
  projectId, assetId, onClose, mode = 'add', fixedType, initial, artifactId, invalidateKey,
}: {
  projectId: string;
  assetId: string;
  onClose: () => void;
  mode?: 'add' | 'edit';
  fixedType?: ArtifactType;
  initial?: Record<string, string | boolean>;
  artifactId?: string;
  invalidateKey?: readonly unknown[];
}) {
  const qc = useQueryClient();
  const isEdit = mode === 'edit';
  const [type, setType] = useState<ArtifactType>(fixedType ?? 'key');
  const [form, setForm] = useState<Record<string, string | boolean>>(initial ?? {});
  const [err, setErr] = useState<string | null>(null);
  const defs = FIELD_DEFS[type];

  const save = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      isEdit
        ? api.patch(`/projects/${projectId}/assets/${assetId}/crypto-artifacts/${type}/${artifactId}`, payload)
        : api.post(`/projects/${projectId}/assets/${assetId}/crypto-artifacts/${type}`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: (invalidateKey ?? ['asset-crypto-inventory', projectId, assetId]) as readonly unknown[] });
      onClose();
    },
    onError: (e) => setErr(e instanceof Error ? e.message : `Failed to ${isEdit ? 'update' : 'add'} artifact.`),
  });

  function setField(name: string, value: string | boolean) {
    setForm((f) => ({ ...f, [name]: value }));
  }

  function submit() {
    setErr(null);
    for (const d of defs) {
      if (d.required && !String(form[d.name] ?? '').trim()) {
        setErr(`${d.label} is required.`);
        return;
      }
    }
    const payload: Record<string, unknown> = {};
    for (const d of defs) {
      const v = form[d.name];
      if (d.kind === 'bool') {
        if (v === true) payload[d.name] = true;
        else if (isEdit) payload[d.name] = false; // allow turning a flag off when editing
      } else {
        const s = String(v ?? '').trim();
        if (s !== '') payload[d.name] = d.kind === 'number' ? Number(s) : s;
        else if (isEdit && !d.required) payload[d.name] = ''; // allow clearing an optional field when editing
      }
    }
    save.mutate(payload);
  }

  return (
    <div className="border border-ink-200 rounded p-4 mb-5 bg-ink-50/40">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-sm font-display text-ink">{isEdit ? 'Edit crypto artifact' : 'Add a crypto artifact'}</h3>
        <span className="text-xs text-ink-500">Linked to this asset</span>
      </div>

      <div className="mb-3">
        <label className={LABEL}>Artifact type</label>
        <select
          aria-label="Artifact type"
          className={INPUT + ' max-w-xs disabled:bg-ink-50 disabled:text-ink-500'}
          value={type}
          disabled={isEdit}
          onChange={(e) => { setType(e.target.value as ArtifactType); setForm({}); setErr(null); }}
        >
          {ARTIFACT_TYPES.map((t) => (
            <option key={t} value={t}>{TYPE_LABELS[t].replace(/s$/, '')}</option>
          ))}
        </select>
        {isEdit && <span className="text-xs text-ink-400 ml-2">Type can't be changed when editing.</span>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {defs.map((d) => (
          <div key={d.name} className={d.kind === 'textarea' ? 'sm:col-span-2' : ''}>
            <label className={LABEL}>
              {d.label}{d.required && <span className="text-red-600"> *</span>}
            </label>
            {d.kind === 'select' ? (
              <select
                aria-label={d.label}
                className={INPUT}
                value={String(form[d.name] ?? '')}
                onChange={(e) => setField(d.name, e.target.value)}
              >
                <option value="">{d.required ? 'Select…' : '—'}</option>
                {d.options!.map((o) => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}
              </select>
            ) : d.kind === 'bool' ? (
              <label className="inline-flex items-center gap-2 text-sm text-ink-700">
                <input
                  type="checkbox"
                  aria-label={d.label}
                  checked={form[d.name] === true}
                  onChange={(e) => setField(d.name, e.target.checked)}
                />
                Yes
              </label>
            ) : d.kind === 'textarea' ? (
              <textarea
                aria-label={d.label}
                className={INPUT}
                rows={2}
                value={String(form[d.name] ?? '')}
                onChange={(e) => setField(d.name, e.target.value)}
              />
            ) : (
              <input
                type={d.kind === 'number' ? 'number' : 'text'}
                aria-label={d.label}
                placeholder={d.placeholder}
                className={INPUT}
                value={String(form[d.name] ?? '')}
                onChange={(e) => setField(d.name, e.target.value)}
              />
            )}
          </div>
        ))}
      </div>

      <p className="text-xs text-ink-500 mt-3">
        Quantum vulnerability and risk are {isEdit ? 're-classified' : 'classified'} automatically
        from the Cryptography Knowledge Base when you save.
      </p>
      {err && <p className="text-xs text-red-700 mt-2">{err}</p>}

      <div className="flex gap-2 mt-3">
        <button type="button" className={BTN_PRIMARY} onClick={submit} disabled={save.isPending}>
          {save.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Save artifact'}
        </button>
        <button type="button" className={BTN_SECONDARY} onClick={onClose} disabled={save.isPending}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Import a CSV of artifacts onto THIS asset. The file needs NO asset column —
// every row links to this asset. Append-only.
// ─────────────────────────────────────────────────────────────────────────
function ImportForm({
  projectId, assetId, onClose,
}: { projectId: string; assetId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [type, setType] = useState<ArtifactType>('key');
  const [csv, setCsv] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [summary, setSummary] = useState<ImportSummary | null>(null);

  const imp = useMutation({
    mutationFn: () =>
      postCsv<ImportSummary>(`/projects/${projectId}/assets/${assetId}/crypto-artifacts/${type}/import`, csv),
    onSuccess: (data) => {
      setSummary(data);
      setErr(null);
      qc.invalidateQueries({ queryKey: ['asset-crypto-inventory', projectId, assetId] });
    },
    onError: (e) => { setErr(e instanceof Error ? e.message : 'Import failed.'); setSummary(null); },
  });

  function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setCsv((ev.target?.result as string) ?? '');
    reader.readAsText(file, 'utf-8');
  }

  function submit() {
    setErr(null);
    if (!csv.trim()) { setErr('Paste CSV or choose a file first.'); return; }
    imp.mutate();
  }

  return (
    <div className="border border-ink-200 rounded p-4 mb-5 bg-ink-50/40">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-sm font-display text-ink">Import crypto artifacts</h3>
        <span className="text-xs text-ink-500">No asset column needed — every row links to this asset</span>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-3">
        <div>
          <label className={LABEL}>Artifact type</label>
          <select
            aria-label="Import artifact type"
            className={INPUT + ' max-w-xs'}
            value={type}
            onChange={(e) => { setType(e.target.value as ArtifactType); setSummary(null); setErr(null); }}
          >
            {ARTIFACT_TYPES.map((t) => (
              <option key={t} value={t}>{TYPE_LABELS[t].replace(/s$/, '')}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={LABEL}>Choose a .csv file</label>
          <input type="file" aria-label="CSV file" accept=".csv,text/csv,text/plain" onChange={onFile} className="text-xs" />
        </div>
        <div>
          <label className={LABEL}>&nbsp;</label>
          <button type="button" className={BTN_SECONDARY} onClick={() => downloadCsvTemplate(type)}>
            Download template
          </button>
        </div>
      </div>

      <label className={LABEL}>…or paste CSV</label>
      <textarea
        aria-label="CSV content"
        className={INPUT + ' font-mono'}
        rows={4}
        placeholder="Header line, then one artifact per row. Use “Download template” for the exact columns — no asset column needed."
        value={csv}
        onChange={(e) => { setCsv(e.target.value); setSummary(null); }}
      />

      {summary && (
        <p className="text-xs text-green-700 mt-2">
          Imported {summary.imported ?? 0} artifact{(summary.imported ?? 0) !== 1 ? 's' : ''}
          {summary.failed ? `, ${summary.failed} row(s) failed` : ''}.
        </p>
      )}
      {err && <p className="text-xs text-red-700 mt-2">{err}</p>}

      <div className="flex gap-2 mt-3">
        <button type="button" className={BTN_PRIMARY} onClick={submit} disabled={imp.isPending}>
          {imp.isPending ? 'Importing…' : 'Import'}
        </button>
        <button type="button" className={BTN_SECONDARY} onClick={onClose} disabled={imp.isPending}>
          {summary ? 'Done' : 'Cancel'}
        </button>
      </div>
    </div>
  );
}

function ArtifactGroupTable({
  group, onEdit, onDelete, deletingId,
}: {
  group: InventoryGroup;
  onEdit: (type: ArtifactType, row: Record<string, unknown>) => void;
  onDelete: (type: ArtifactType, id: string, name: string) => void;
  deletingId: string | null;
}) {
  return (
    <div>
      <h3 className="text-sm font-display text-ink mb-2">
        {TYPE_LABELS[group.type]} <span className="text-ink-400">({group.count})</span>
      </h3>
      {/* overflow-x-auto, not overflow-hidden: a wide table scrolls sideways instead of being cut off. */}
      <div className="border border-ink-200 rounded overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="bg-ink-50 text-ink-500 uppercase tracking-wide">
            <tr>
              <th className="px-3 py-2 text-left">Name</th>
              {group.type === 'key' && (
                <>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-left">Algorithm</th>
                  <th className="px-3 py-2 text-left">Length</th>
                  <th className="px-3 py-2 text-left">Curve</th>
                  <th className="px-3 py-2 text-left">Fingerprint</th>
                </>
              )}
              {group.type === 'certificate' && (
                <>
                  <th className="px-3 py-2 text-left">Subject</th>
                  <th className="px-3 py-2 text-left">Sig algo</th>
                  <th className="px-3 py-2 text-left">Hash</th>
                  <th className="px-3 py-2 text-left">PK algo</th>
                </>
              )}
              {group.type === 'protocol' && (
                <>
                  <th className="px-3 py-2 text-left">Family</th>
                  <th className="px-3 py-2 text-left">Version</th>
                  <th className="px-3 py-2 text-left">State</th>
                  <th className="px-3 py-2 text-left">SNMPv3 level</th>
                  <th className="px-3 py-2 text-left">Parts (each rated)</th>
                </>
              )}
              {group.type === 'cipher_suite' && (
                <>
                  <th className="px-3 py-2 text-left">Suite</th>
                  <th className="px-3 py-2 text-left">IANA</th>
                  <th className="px-3 py-2 text-left">TLS versions</th>
                  <th className="px-3 py-2 text-left">State</th>
                  <th className="px-3 py-2 text-left">Parts (each rated)</th>
                </>
              )}
              {group.type === 'library' && (
                <>
                  <th className="px-3 py-2 text-left">Vendor</th>
                  <th className="px-3 py-2 text-left">Version</th>
                </>
              )}
              {group.type === 'store' && (
                <>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-left">Vendor</th>
                  <th className="px-3 py-2 text-left">FIPS</th>
                </>
              )}
              {group.type === 'ssh_algorithm' && (
                <>
                  <th className="px-3 py-2 text-left">Role</th>
                  <th className="px-3 py-2 text-left">SSH version</th>
                  <th className="px-3 py-2 text-left">Inventory rating</th>
                </>
              )}
              {group.type === 'algorithm' && (
                <>
                  <th className="px-3 py-2 text-left">File</th>
                  <th className="px-3 py-2 text-left">Seen</th>
                  <th className="px-3 py-2 text-left">Inventory rating</th>
                </>
              )}
              {group.type === 'signature' && (
                <>
                  <th className="px-3 py-2 text-left">Algorithm</th>
                  <th className="px-3 py-2 text-left">Length</th>
                  <th className="px-3 py-2 text-left">Curve</th>
                </>
              )}
              <th className="px-3 py-2 text-left">KB status</th>
              <th className="px-3 py-2 text-left">Risk</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {group.rows.map((r, i) => (
              <ArtifactRow
                key={String(r['id'] ?? i)}
                type={group.type}
                row={r}
                onEdit={onEdit}
                onDelete={onDelete}
                deleting={deletingId === String(r['id'])}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ArtifactRow({
  type, row, onEdit, onDelete, deleting,
}: {
  type: GroupType;
  row: Record<string, unknown>;
  onEdit: (type: ArtifactType, row: Record<string, unknown>) => void;
  onDelete: (type: ArtifactType, id: string, name: string) => void;
  deleting: boolean;
}) {
  const s = (k: string) => (row[k] as string | null | undefined) ?? '';
  const n = (k: string) => row[k] as number | null | undefined;
  const id = String(row['id'] ?? '');

  return (
    <tr>
      <td className="px-3 py-2 font-medium text-ink">{s('name') || <span className="text-ink-300 font-normal">(unnamed)</span>}</td>

      {type === 'key' && (
        <>
          <td className="px-3 py-2 text-ink-700">{s('key_type')}</td>
          <td className="px-3 py-2 font-mono text-ink-700">{s('algorithm_canonical') || s('algorithm_raw')}</td>
          <td className="px-3 py-2 font-mono">{n('key_length') ?? '—'}</td>
          <td className="px-3 py-2 font-mono">{s('curve_name') || '—'}</td>
          <td className="px-3 py-2 font-mono" title={s('fingerprint_sha256')}>{s('fingerprint_sha256') ? `${s('fingerprint_sha256').slice(0, 12)}…` : '—'}</td>
        </>
      )}
      {type === 'certificate' && (
        <>
          <td className="px-3 py-2 text-ink-700"><div className="truncate max-w-xs" title={s('subject')}>{s('subject') || '—'}</div></td>
          <td className="px-3 py-2 font-mono">{s('signature_algorithm_canonical') || s('signature_algorithm_raw')}</td>
          <td className="px-3 py-2 font-mono">{s('hashing_algorithm_canonical') || '—'}</td>
          <td className="px-3 py-2 font-mono">
            {s('public_key_algorithm_canonical') || s('public_key_algorithm_raw')}
            {n('public_key_length') ? `-${n('public_key_length')}` : ''}
          </td>
        </>
      )}
      {type === 'protocol' && (
        <>
          <td className="px-3 py-2 text-ink-700">{s('protocol_family')}</td>
          <td className="px-3 py-2 font-mono">{s('protocol_version_canonical') || s('protocol_version_raw')}</td>
          <td className="px-3 py-2">{s('configured_state') || '—'}</td>
          <td className="px-3 py-2 font-mono">{s('snmp_security_level') || '—'}</td>
          <td className="px-3 py-2"><CryptoPartsCell row={row} /></td>
        </>
      )}
      {type === 'cipher_suite' && (
        <>
          <td className="px-3 py-2 font-mono text-ink-700">{s('suite_canonical') || s('suite_raw')}</td>
          <td className="px-3 py-2 font-mono">{s('iana_code') || '—'}</td>
          <td className="px-3 py-2 font-mono">{s('tls_version') || '—'}</td>
          <td className="px-3 py-2">{s('configured_state') || '—'}</td>
          <td className="px-3 py-2"><CryptoPartsCell row={row} /></td>
        </>
      )}
      {type === 'library' && (
        <>
          <td className="px-3 py-2 text-ink-700">{s('vendor') || '—'}</td>
          <td className="px-3 py-2 font-mono">{s('version_canonical') || s('version_raw')}</td>
        </>
      )}
      {type === 'store' && (
        <>
          <td className="px-3 py-2 text-ink-700">{s('store_type')}</td>
          <td className="px-3 py-2 text-ink-700">{s('vendor') || '—'}</td>
          <td className="px-3 py-2">{s('fips_level')}</td>
        </>
      )}
      {type === 'ssh_algorithm' && (
        <>
          <td className="px-3 py-2 text-ink-700">{s('algorithm_role').replace(/_/g, ' ')}</td>
          <td className="px-3 py-2 font-mono">{s('protocol_version') || '—'}</td>
          <td className="px-3 py-2">{Number(row['classically_broken']) ? 'Broken classically' : s('quantum_vulnerability').replace(/_/g, ' ')}</td>
        </>
      )}
      {type === 'algorithm' && (
        <>
          <td className="px-3 py-2 font-mono text-ink-500 break-all">{s('file_path') || '—'}</td>
          <td className="px-3 py-2 font-mono">{n('observation_count') ?? '—'}</td>
          <td className="px-3 py-2">{Number(row['classically_broken']) ? 'Broken classically' : s('quantum_vulnerability').replace(/_/g, ' ')}</td>
        </>
      )}
      {type === 'signature' && (
        <>
          <td className="px-3 py-2 font-mono">{s('signature_algorithm_canonical') || s('signature_algorithm_raw')}</td>
          <td className="px-3 py-2 font-mono">{n('key_length') ?? '—'}</td>
          <td className="px-3 py-2 font-mono">{s('curve_name') || '—'}</td>
        </>
      )}

      <td className="px-3 py-2">
        <span className={`text-xs px-2 py-0.5 rounded ${kbStatusStyle(s('kb_lookup_status'))}`}>
          KB {s('kb_lookup_status').toLowerCase()}
        </span>
      </td>
      <td className="px-3 py-2">
        <RiskBadge level={s('risk_level')} />
      </td>
      <td className="px-3 py-2 text-right whitespace-nowrap">
        {type !== 'ssh_algorithm' && type !== 'algorithm' && (
        <button
          type="button"
          className="text-xs text-ink-700 border border-ink-200 rounded px-2 py-1 hover:bg-ink-50 disabled:opacity-50 mr-2"
          onClick={() => onEdit(type, row)}
          disabled={deleting}
        >
          Edit
        </button>)}
        <button
          type="button"
          className="text-xs text-red-700 border border-red-200 rounded px-2 py-1 hover:bg-red-50 disabled:opacity-50"
          onClick={() => onDelete(type as ArtifactType, id, s('name'))}
          disabled={deleting}
        >
          {deleting ? 'Removing…' : 'Remove'}
        </button>
      </td>
    </tr>
  );
}

function kbStatusStyle(status: string): string {
  switch (status) {
    case 'Resolved': return 'bg-green-50 text-green-700';
    case 'NeedsReview': return 'bg-amber-50 text-amber-700';
    default: return 'bg-ink-50 text-ink-500';
  }
}

function RiskBadge({ level }: { level: string }) {
  if (!level) return <span className="text-ink-300 text-xs">—</span>;
  const styles: Record<string, string> = {
    No_risk: 'bg-green-100 text-green-800',
    Very_low: 'bg-green-50 text-green-700',
    Low: 'bg-lime-50 text-lime-700',
    Moderate: 'bg-yellow-50 text-yellow-700',
    High: 'bg-orange-50 text-orange-700',
    Very_high: 'bg-red-50 text-red-700',
    Critical: 'bg-red-100 text-red-800 font-medium',
    NA: 'bg-ink-50 text-ink-500',
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded ${styles[level] ?? 'bg-ink-50 text-ink-500'}`}>
      {level.replace('_', ' ')}
    </span>
  );
}
